import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { Helmet } from 'react-helmet'
import DatePicker from 'react-datepicker'
import moment from 'moment'
import { createNewCoupon, resetNewCouponForm, resetCouponStatus } from '../../actions/adminCoupons'

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#6d7175',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  display: 'block',
  marginBottom: 4,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #e1e3e5',
  borderRadius: 6,
  fontSize: '0.85rem',
  color: '#202223',
  background: '#fff',
  outline: 'none',
}

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  border: '1px solid #ec174f',
}

const NewCoupon = ({ coupons, createNewCoupon, resetNewCouponForm, resetCouponStatus, history }: any) => {
  const [code, setCode] = useState('')
  const [isPercent, setIsPercent] = useState(true)
  const [amount, setAmount] = useState('')
  const [minimumSpend, setMinimumSpend] = useState('')
  const [expireDate, setExpireDate] = useState('')
  const [errors, setErrors] = useState<any>({})
  const [showSuccess, setShowSuccess] = useState(false)
  const [isOneTime, setIsOneTime] = useState(true)

  const prevCouponsRef = useRef(coupons)

  useEffect(() => {
    return () => { resetNewCouponForm() }
  }, [])

  useEffect(() => {
    const prev = prevCouponsRef.current
    if (coupons.errors !== prev.errors) setErrors(coupons.errors)
    if (coupons.newCouponCreated !== prev.newCouponCreated && coupons.newCouponCreated) {
      setShowSuccess(true)
    }
    prevCouponsRef.current = coupons
  }, [coupons])

  const onChange = (e: any) => {
    const { name, value } = e.target
    if (name === 'code') setCode(value)
    if (name === 'amount') setAmount(value)
    if (name === 'minimumSpend') setMinimumSpend(value)
    setShowSuccess(false)
    if (coupons.newCouponCreated) resetCouponStatus()
  }

  const handleNewCode = () => {
    createNewCoupon({
      code,
      isPercent,
      amount,
      expireDate,
      isActive: true,
      minimumSpend: minimumSpend === '' ? 0 : minimumSpend,
      isOneTime,
    })
  }

  return (
    <>
      <Helmet>
        <title>New Coupon - Frozberries Victoria</title>
      </Helmet>

      <div className="admin-card" style={{ maxWidth: 560 }}>
        <div className="admin-card-header">
          <p className="admin-card-title">New Coupon</p>
        </div>
        <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          <div>
            <label style={fieldLabelStyle}>Discount Code</label>
            <input
              style={errors.code ? inputErrorStyle : inputStyle}
              placeholder="e.g. SPRINGSALE"
              onChange={onChange}
              value={code}
              name="code"
            />
            {errors.code && <span style={{ fontSize: '0.75rem', color: '#ec174f' }}>{errors.code}</span>}
          </div>

          <div>
            <label style={fieldLabelStyle}>Discount Type</label>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  checked={isPercent}
                  onChange={() => setIsPercent(true)}
                />
                Percentage
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="discountType"
                  checked={!isPercent}
                  onChange={() => setIsPercent(false)}
                />
                Fixed amount
              </label>
            </div>
          </div>

          <div>
            <label style={fieldLabelStyle}>Usage</label>
            <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="usageType"
                  checked={isOneTime}
                  onChange={() => setIsOneTime(true)}
                />
                One time
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="usageType"
                  checked={!isOneTime}
                  onChange={() => setIsOneTime(false)}
                />
                Unlimited
              </label>
            </div>
          </div>

          <div>
            <label style={fieldLabelStyle}>Discount Value ({isPercent ? '%' : '$'})</label>
            <input
              style={errors.amount ? inputErrorStyle : inputStyle}
              placeholder="e.g. 10"
              onChange={onChange}
              value={amount}
              name="amount"
              type="number"
            />
            {errors.amount && <span style={{ fontSize: '0.75rem', color: '#ec174f' }}>{errors.amount}</span>}
          </div>

          <div>
            <label style={fieldLabelStyle}>Minimum Spend ($)</label>
            <input
              style={errors.minimumSpend ? inputErrorStyle : inputStyle}
              placeholder="0 if not required"
              onChange={onChange}
              value={minimumSpend}
              name="minimumSpend"
              type="number"
            />
            {errors.minimumSpend && <span style={{ fontSize: '0.75rem', color: '#ec174f' }}>{errors.minimumSpend}</span>}
          </div>

          <div>
            <label style={fieldLabelStyle}>Expiry Date</label>
            <DatePicker
              selected={expireDate}
              onChange={(date: any) => setExpireDate(date)}
              placeholderText="Select a date"
              minDate={moment().toDate()}
              excludeDates={[new Date()]}
            />
            {errors.expireDate && <span style={{ fontSize: '0.75rem', color: '#ec174f', display: 'block', marginTop: 4 }}>{errors.expireDate}</span>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              className="admin-dropdown-trigger"
              style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f' }}
              onClick={handleNewCode}
            >
              {coupons.createButtonLoading ? 'Creating…' : 'Create Coupon'}
            </button>
            {showSuccess && (
              <>
                <span className="status-badge confirmed" style={{ fontSize: '0.78rem' }}>Created!</span>
                <span
                  style={{ fontSize: '0.78rem', color: '#ec174f', cursor: 'pointer' }}
                  onClick={() => history.push('/adminpanel/coupons')}
                >
                  Back to Coupons →
                </span>
              </>
            )}
          </div>

        </div>
      </div>
    </>
  )
}

const mapStateToProps = (state: any) => ({
  coupons: state.adminCouponReducer,
})

export default connect(mapStateToProps, { createNewCoupon, resetNewCouponForm, resetCouponStatus })(NewCoupon)
